import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, Sparkle, FolderOpen, Calendar, SignOut, CreditCard, Buildings, MapTrifold } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import { INDUSTRIES, getIndustry } from "@/lib/industries";
import logger from "@/lib/logger";

const TIER_LABEL = { free: "Free", base: "Starter", pro: "Pro", enterprise: "Enterprise" };
const TIER_COLOR = { free: "#52525B", base: "#0020F5", pro: "#09090B", enterprise: "#7C3AED" };

export default function Account() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [polling, setPolling] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [industry, setIndustry] = useState(user?.industry || "general");
  const [savingIndustry, setSavingIndustry] = useState(false);
  const [retakingTour, setRetakingTour] = useState(false);

  const retakeTour = async () => {
    setRetakingTour(true);
    try {
      // Clear local "seen" flag so GuidedTour re-triggers on mount.
      // localStorage.removeItem can throw in Safari private mode or when storage is full —
      // we intentionally ignore that since the server-side flag below is the source of truth.
      try { window.localStorage.removeItem("arissai.tour.completed.v1"); } catch { /* private-mode safe */ }
      // Server flag so the tour doesn't re-show on another device
      await api.patch("/auth/me", { tour_completed: false });
      toast.success("Restarting the guided tour…");
      // Hard navigation with ?tour=1 so the overlay opens
      window.location.assign("/dashboard?tour=1");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restart the tour");
      setRetakingTour(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/billing/me");
      setBilling(data);
    } catch (err) {
      // Non-fatal: billing summary widget falls back to defaults
      logger.warn("Failed to load billing summary:", err?.response?.data?.detail || err?.message);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/account`,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Could not open billing portal.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not open billing portal.";
      toast.error(msg);
    } finally {
      setPortalLoading(false);
    }
  };

  const saveIndustry = async (next) => {
    setIndustry(next);
    setSavingIndustry(true);
    try {
      await api.patch("/auth/me", { industry: next });
      await refreshUser();
      toast.success(`Industry updated to ${getIndustry(next).label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update industry");
      setIndustry(user?.industry || "general");
    } finally {
      setSavingIndustry(false);
    }
  };

  useEffect(() => {
    if (user?.industry && user.industry !== industry) setIndustry(user.industry);
  }, [user?.industry]); // eslint-disable-line

  useEffect(() => { load(); }, []);

  // Poll Stripe status when returning from checkout
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    setPolling(true);
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(`Payment successful! You're now on ${TIER_LABEL[data.tier] || data.tier}.`);
          await load();
          // refresh user object in localStorage / context
          try {
            const me = await api.get("/auth/me");
            localStorage.setItem("arissai_user", JSON.stringify(me.data));
            window.location.replace("/account"); // strip session_id
          } catch (err) {
            logger.warn("Could not refresh user after payment:", err?.message);
          }
          setPolling(false);
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired.");
          setPolling(false);
          return;
        }
        if (attempts >= 6) {
          toast.info("Payment is still processing. Refresh in a minute.");
          setPolling(false);
          return;
        }
        setTimeout(poll, 2500);
      } catch (err) {
        logger.warn("Billing status poll failed:", err?.message);
        setPolling(false);
      }
    };
    poll();
  }, [params]); // eslint-disable-line

  const tier = billing?.tier || user?.subscription_tier || "free";
  const limits = billing?.limits;
  const usage = billing?.usage;
  const expiresAt = billing?.expires_at;

  return (
    <div className="min-h-screen bg-zinc-100">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <BrandMark size={36} testid="account-brand" tagline="AI-Driven Project Intelligence" />
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-sm font-medium hover:text-zinc-950 inline-flex items-center gap-1.5" data-testid="account-dashboard-link">
              <ArrowLeft size={14} weight="bold" /> Dashboard
            </Link>
            <button onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-300 text-sm font-medium hover:bg-zinc-100"
              data-testid="account-logout-button">
              <SignOut size={14} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
        <div className="mb-8">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Account</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">Subscription</h1>
        </div>

        {polling && (
          <div className="mb-6 bg-[#E5EAFF] border border-[#0020F5] p-4 text-sm text-[#0020F5] font-semibold" data-testid="polling-banner">
            <Sparkle size={14} weight="fill" className="inline -mt-0.5 mr-2" /> Confirming payment with Stripe…
          </div>
        )}

        {/* Current plan card */}
        <div className="grid lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          <div className="bg-white p-6 lg:col-span-2">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Current plan</div>
            <div className="mt-2 flex items-center gap-3">
              <div className="w-3 h-3" style={{ background: TIER_COLOR[tier] }} />
              <div className="font-display font-black text-3xl">{TIER_LABEL[tier]}</div>
              <span className="inline-flex items-center gap-1.5 px-2 h-6 bg-[#DCFCE7] text-[#16A34A] text-[10px] font-bold uppercase tracking-wider">
                <CheckCircle size={11} weight="fill" /> Active
              </span>
            </div>
            {expiresAt && (
              <div className="mt-3 text-sm text-zinc-600 inline-flex items-center gap-2">
                <Calendar size={14} weight="duotone" /> Renews / expires <span className="font-mono">{new Date(expiresAt).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}</span>
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing" className="h-11 px-5 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800" data-testid="upgrade-button">
                {tier === "free" ? "Choose a plan" : tier === "enterprise" ? "Manage plan" : "Upgrade plan"}
              </Link>
              {tier !== "free" && (
                <Link to="/pricing" className="h-11 px-5 inline-flex items-center gap-2 border border-zinc-300 text-sm font-medium hover:bg-zinc-100" data-testid="renew-button">
                  Renew or extend
                </Link>
              )}
              {tier !== "free" && (
                <button
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="h-11 px-5 inline-flex items-center gap-2 border border-zinc-300 text-sm font-medium hover:bg-zinc-100 disabled:opacity-60"
                  data-testid="manage-billing-button"
                >
                  <CreditCard size={14} weight="bold" />
                  {portalLoading ? "Opening Stripe…" : "Manage billing"}
                </button>
              )}
            </div>
            {tier !== "free" && (
              <p className="mt-3 text-xs text-zinc-500 max-w-md">
                Update card, download invoices, or cancel your subscription via the secure Stripe Customer Portal.
              </p>
            )}
          </div>

          <div className="bg-white p-6">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Usage</div>
            <div className="mt-3 space-y-3">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-1.5 text-zinc-700"><FolderOpen size={14} weight="duotone" /> Projects</span>
                  <span className="font-mono">{usage?.projects ?? 0} / {limits?.max_projects ?? "—"}</span>
                </div>
                <div className="w-full h-1.5 bg-zinc-100 mt-2">
                  <div className="h-1.5 bg-zinc-950" style={{ width: `${Math.min(100, ((usage?.projects || 0) / (limits?.max_projects || 1)) * 100)}%` }} />
                </div>
              </div>
              <div className="text-xs text-zinc-600 space-y-1.5 mt-4">
                <div className="flex items-center justify-between">
                  <span>PMO Weekly Brief (in-app)</span>
                  <span className={limits?.weekly_brief ? "text-[#16A34A] font-bold" : "text-zinc-400"}>{limits?.weekly_brief ? "Included" : "Locked"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Email delivery</span>
                  <span className={limits?.email_brief ? "text-[#16A34A] font-bold" : "text-zinc-400"}>{limits?.email_brief ? "Included" : "Locked"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Priority AI</span>
                  <span className={limits?.priority_ai ? "text-[#16A34A] font-bold" : "text-zinc-400"}>{limits?.priority_ai ? "Included" : "Locked"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white border border-zinc-200 p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 inline-flex items-center gap-2">
            <Buildings size={12} weight="duotone" /> Industry profile
          </div>
          <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Tailors lens analysis & dashboard vocabulary</label>
              <select
                value={industry}
                onChange={(e) => saveIndustry(e.target.value)}
                disabled={savingIndustry}
                className="w-full h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950 disabled:opacity-60"
                data-testid="account-industry-select"
              >
                {INDUSTRIES.map((i) => (
                  <option key={i.slug} value={i.slug}>{i.label}</option>
                ))}
              </select>
            </div>
            <div className="text-xs text-zinc-600 sm:max-w-xs sm:text-right">
              <span className="inline-block w-2 h-2 mr-2" style={{ background: getIndustry(industry).accent }} />
              {getIndustry(industry).valueProp}
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white border border-zinc-200 p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 inline-flex items-center gap-2">
            <MapTrifold size={12} weight="duotone" /> Product tour
          </div>
          <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-4 items-center">
            <div className="text-sm text-zinc-600">
              Want a refresher on the six lenses and how ARISSAI fits into your workflow? Replay the 60-second guided walkthrough — your data stays untouched.
            </div>
            <button
              type="button"
              onClick={retakeTour}
              disabled={retakingTour}
              className="inline-flex items-center gap-2 h-11 px-5 bg-zinc-950 text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5] disabled:opacity-60"
              data-testid="account-retake-tour"
            >
              <Sparkle size={12} weight="fill" />
              {retakingTour ? "Restarting…" : "Re-take the tour"}
            </button>
          </div>
        </div>

        <div className="mt-8 bg-white border border-zinc-200 p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Account details</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-zinc-500 text-xs">Name</div>
              <div className="font-semibold">{user?.full_name}</div>
            </div>
            <div>
              <div className="text-zinc-500 text-xs">Email</div>
              <div className="font-mono">{user?.email}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
